import { GripVertical, List as ListIcon, Lock, Plus, Table2, Trash2, Type } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import type { ComposerItem } from '@/hooks/useEmail';
import { RM_ADDABLE_BLOCK_TYPES } from '@/hooks/useEmail';
import { BLOCK_TYPE_LABELS, type EmailBlock, type EmailBlockType } from '@/lib/emailBlocks';
import cn from '@/utils/cn';

const ADD_BLOCK_ICONS: Record<EmailBlockType, typeof Type> = {
  heading: Type,
  paragraph: Type,
  list: ListIcon,
  table: Table2,
  bankDetails: Type,
  productsTable: Type,
  billingTotals: Type,
  tcLinks: Type,
  additionalArns: Type,
  installments: Type,
  raw: Type,
};

const fieldClass =
  'w-full min-w-0 rounded-md border border-input bg-background px-2.5 py-1.5 text-base focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:text-xs';

interface RmBlockFieldsProps {
  block: EmailBlock;
  onChange: (next: EmailBlock) => void;
}

// Editable fields for the only block types the RM can add (Fix 2) - a
// pared-down version of the admin editor's BlockCard (no variable-insert
// wiring, since these blocks are the RM's own free-text content).
function RmBlockFields({ block, onChange }: RmBlockFieldsProps) {
  if (block.type === 'paragraph') {
    return (
      <div className="flex flex-col gap-1.5">
        <textarea
          value={block.text}
          onChange={(e) => onChange({ ...block, text: e.target.value })}
          rows={4}
          placeholder="Type a paragraph..."
          className={cn(fieldClass, 'min-h-24 resize-y')}
        />
        <div className="flex gap-4">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox
              checked={block.bold}
              onChange={(e) => onChange({ ...block, bold: e.target.checked })}
            />
            Bold
          </label>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox
              checked={block.italic ?? false}
              onChange={(e) => onChange({ ...block, italic: e.target.checked })}
            />
            Italic
          </label>
        </div>
      </div>
    );
  }

  if (block.type === 'list') {
    return (
      <div className="flex flex-col gap-1.5">
        {block.items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="flex items-center gap-1.5">
            <Input
              value={item}
              onChange={(e) =>
                onChange({
                  ...block,
                  items: block.items.map((x, j) => (j === i ? e.target.value : x)),
                })
              }
              placeholder={`Point ${i + 1}`}
              className="h-9 min-w-0 flex-1 text-base sm:h-8 sm:text-xs"
            />
            <button
              type="button"
              onClick={() => onChange({ ...block, items: block.items.filter((_, j) => j !== i) })}
              disabled={block.items.length <= 1}
              aria-label="Remove point"
              className="-m-2 inline-flex h-11 w-11 shrink-0 items-center justify-center p-2 text-muted-foreground transition-colors hover:text-danger sm:h-8 sm:w-8 disabled:opacity-30"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => onChange({ ...block, items: [...block.items, ''] })}
          className="flex w-fit items-center gap-1 text-xs font-semibold text-primary"
        >
          <Plus className="h-3.5 w-3.5" />
          Add point
        </button>
      </div>
    );
  }

  if (block.type === 'table') {
    return (
      <div className="flex flex-col gap-1.5">
        {/* ONE horizontal scroll container wrapping the header row and every
            body row, so they all share a single `scrollLeft`. Per-row
            `overflow-x-auto` would give each row its own scroll offset, and a
            row scrolled out of step puts the RM's cells under the wrong column
            label. The `w-max min-w-full` inner track is what actually
            overflows on a phone (fixed 7.5rem columns) while still filling the
            container when the table is narrow. */}
        <div className="w-full overflow-x-auto pb-1">
          <div className="flex w-max min-w-full flex-col gap-1.5">
            <div className="flex items-start gap-1.5">
              {block.headers.map((header, c) => (
                // eslint-disable-next-line react/no-array-index-key
                <div
                  key={c}
                  className="flex w-[7.5rem] shrink-0 flex-col gap-0.5 sm:w-auto sm:min-w-[7.5rem] sm:flex-1"
                >
                  <Input
                    value={header}
                    onChange={(e) =>
                      onChange({
                        ...block,
                        headers: block.headers.map((x, i) => (i === c ? e.target.value : x)),
                      })
                    }
                    placeholder={`Column ${c + 1}`}
                    className="h-9 text-base font-semibold sm:h-8 sm:text-xs"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...block,
                        headers: block.headers.filter((_, i) => i !== c),
                        rows: block.rows.map((row) => row.filter((_, i) => i !== c)),
                      })
                    }
                    disabled={block.headers.length <= 1}
                    aria-label={`Delete column ${c + 1}`}
                    className="text-[10px] text-muted-foreground transition-colors hover:text-danger disabled:opacity-20"
                  >
                    Del col
                  </button>
                </div>
              ))}
              {/* Spacer matching the body rows' remove-row button, so the
                  header columns stay the same width as the cell columns. */}
              <div aria-hidden className="-m-2 h-11 w-11 shrink-0 sm:h-8 sm:w-8" />
            </div>
            {block.rows.map((row, r) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={r} className="flex items-center gap-1.5">
                {row.map((cell, c) => (
                  <Input
                    // eslint-disable-next-line react/no-array-index-key
                    key={c}
                    value={cell}
                    onChange={(e) =>
                      onChange({
                        ...block,
                        rows: block.rows.map((rr, ri) =>
                          ri === r ? rr.map((cc, ci) => (ci === c ? e.target.value : cc)) : rr,
                        ),
                      })
                    }
                    className="h-9 w-[7.5rem] shrink-0 text-base sm:h-8 sm:w-auto sm:min-w-[7.5rem] sm:flex-1 sm:text-xs"
                  />
                ))}
                <button
                  type="button"
                  onClick={() =>
                    onChange({ ...block, rows: block.rows.filter((_, ri) => ri !== r) })
                  }
                  aria-label="Remove row"
                  className="-m-2 inline-flex h-11 w-11 shrink-0 items-center justify-center p-2 text-muted-foreground transition-colors hover:text-danger sm:h-8 sm:w-8"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
        {/* Outside the scroll container so the add controls stay reachable
            no matter how far the table is scrolled. */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() =>
              onChange({
                ...block,
                headers: [...block.headers, `Column ${block.headers.length + 1}`],
                rows: block.rows.map((row) => [...row, '']),
              })
            }
            className="flex items-center gap-1 text-xs font-semibold text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Column
          </button>
          <button
            type="button"
            onClick={() =>
              onChange({ ...block, rows: [...block.rows, block.headers.map(() => '')] })
            }
            className="flex items-center gap-1 text-xs font-semibold text-primary"
          >
            <Plus className="h-3.5 w-3.5" />
            Row
          </button>
        </div>
      </div>
    );
  }

  return null;
}

export interface EmailBlockComposerProps {
  items: ComposerItem[];
  dragIndex: number | null;
  onDragStart: (index: number) => void;
  onDragEnd: () => void;
  onDragOverIndex: (index: number) => void;
  onUpdateRmBlock: (index: number, block: EmailBlock) => void;
  onRemoveItem: (index: number) => void;
  onAddBlock: (type: EmailBlockType) => void;
}

// The Sales RM-facing counterpart to the admin editor's block list (Fix 2):
// the admin's template content shows as locked, re-orderable cards (content
// can't be touched, only moved), and the RM can add/edit/remove their own
// paragraph/list/table blocks anywhere in the sequence.
export function EmailBlockComposer({
  items,
  dragIndex,
  onDragStart,
  onDragEnd,
  onDragOverIndex,
  onUpdateRmBlock,
  onRemoveItem,
  onAddBlock,
}: EmailBlockComposerProps) {
  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div
          key={item.id}
          onDragOver={(e) => {
            e.preventDefault();
            onDragOverIndex(index);
          }}
          className={cn(
            'rounded-lg border p-3 transition-opacity',
            item.kind === 'locked' ? 'border-border bg-muted/30' : 'border-border bg-background',
            dragIndex === index && 'opacity-40',
          )}
        >
          <div className="mb-2 flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <span
                draggable
                onDragStart={() => onDragStart(index)}
                onDragEnd={onDragEnd}
                aria-label="Drag to reorder"
                className="cursor-grab active:cursor-grabbing"
              >
                <GripVertical className="h-4 w-4" />
              </span>
              {item.kind === 'locked' ? (
                <>
                  <Lock className="h-3.5 w-3.5" />
                  <span className="text-[11px] font-bold uppercase tracking-wide">
                    Admin template - {BLOCK_TYPE_LABELS[item.type as EmailBlockType] ?? item.type}
                  </span>
                </>
              ) : (
                <>
                  {(() => {
                    const Icon = ADD_BLOCK_ICONS[item.block.type];
                    return <Icon className="h-3.5 w-3.5" />;
                  })()}
                  <span className="text-[11px] font-bold uppercase tracking-wide">
                    Your content - {BLOCK_TYPE_LABELS[item.block.type]}
                  </span>
                </>
              )}
            </div>
            {item.kind === 'rm' && (
              <button
                type="button"
                onClick={() => onRemoveItem(index)}
                aria-label="Remove block"
                className="-m-2 inline-flex h-11 w-11 shrink-0 items-center justify-center p-2 text-muted-foreground transition-colors hover:text-danger sm:h-8 sm:w-8"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {item.kind === 'locked' ? (
            // The scroll container stays interactive; only its contents are
            // made non-clickable. `inert` was tried here and is wrong twice
            // over: it hides the email the RM is about to send from assistive
            // tech, and it disables hit-testing on the very element that has
            // to be scrollable, so a wide locked table stays unreachable.
            <div className="overflow-x-auto text-sm">
              <div
                className="pointer-events-none w-max min-w-full"
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: item.html }}
              />
            </div>
          ) : (
            <RmBlockFields block={item.block} onChange={(next) => onUpdateRmBlock(index, next)} />
          )}
        </div>
      ))}

      <div className="flex flex-wrap gap-2 pt-1">
        {RM_ADDABLE_BLOCK_TYPES.map((type) => {
          const Icon = ADD_BLOCK_ICONS[type];
          return (
            <Button
              key={type}
              type="button"
              variant="outline"
              size="sm"
              onClick={() => onAddBlock(type)}
            >
              <Icon className="h-3.5 w-3.5" /> Add {BLOCK_TYPE_LABELS[type]}
            </Button>
          );
        })}
      </div>
    </div>
  );
}
